import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ResultView } from "@/components/ResultView";
import { AiSummary } from "@/components/AiSummary";
import { OnboardingCard } from "@/components/OnboardingCard";
import { ApprovalCard } from "@/components/ApprovalCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const AFF = { sda: "Seventh-day Adventist", nondenom: "Non-denominational", baptist: "Baptist", catholic: "Catholic", pentecostal: "Pentecostal", other: "Other", none: "Not affiliated with a church", private: "Prefer not to say" };

export default function VolunteerDetail() {
  const { church } = useAuth();
  const { vid } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [disc, setDisc] = useState(null);

  useEffect(() => {
    api.get(`/volunteers/${vid}`).then((r) => setV(r.data)).catch((e) => {
      toast.error(e.response?.data?.detail || "Could not open this volunteer. Please try again.");
      navigate("/volunteers");
    });
    api.get("/reference").then((r) => setDisc(r.data));
  }, [vid, navigate]);

  const remove = async () => {
    await api.delete(`/volunteers/${vid}`);
    toast.success("Volunteer removed");
    navigate("/volunteers");
  };

  if (!v) return null;
  const paid = church?.billing?.can_collect;
  return (
    <div className="fade-up">
      <Link to="/volunteers" className="text-sm text-muted-ink hover:text-teal flex items-center gap-1" data-testid="back-volunteers"><ArrowLeft size={14} /> All volunteers</Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-4 mb-8">
        <div>
          <h1 className="font-display text-navy text-4xl font-semibold tracking-tight" data-testid="volunteer-name">{v.name}</h1>
          <p className="text-muted-ink mt-2" data-testid="volunteer-contact">{v.email}{v.phone ? ` · ${v.phone}` : ""} · {new Date(v.created_at).toLocaleString()}</p>
          {v.inactive_team_ids?.length > 0 && <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2 mt-2" data-testid="inactive-team-flag">This volunteer's preferred or approved team is no longer active in your ministry library. Their history is kept.</p>}
          {v.personal_affiliation && <p className="text-sm text-muted-ink mt-1" data-testid="volunteer-affiliation">Religious affiliation: <span className="text-navy">{AFF[v.personal_affiliation]}{v.personal_affiliation_other ? ` (${v.personal_affiliation_other})` : ""}</span></p>}
        </div>
        <div className="flex gap-3">
        {paid && <a href={`${API}/public/results/${vid}/pdf?lang=${v.lang || "en"}`} className="btn-navy !py-2 flex items-center gap-2" data-testid="admin-download-pdf-btn"><Download size={16} /> PDF{v.lang === "es" ? " (ES)" : ""}</a>}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="btn-ghost flex items-center gap-2 !py-2 text-red-700" data-testid="delete-volunteer-btn"><Trash2 size={16} /> Remove</button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {v.name}?</AlertDialogTitle>
              <AlertDialogDescription>This permanently deletes their assessment result.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove} data-testid="confirm-delete-volunteer-btn">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </div>
      </div>
      <div className="space-y-6">
        {paid && <ApprovalCard v={v} setV={setV} canEdit={church.role !== "viewer"} />}
        {paid && <OnboardingCard v={v} setV={setV} canEdit={church.role !== "viewer"} pro={church.billing.pro} />}
        {church?.billing?.pro && <AiSummary vid={vid} lang="en" />}
        <ResultView v={v} reference={disc} />
      </div>
    </div>
  );
}
